/**
 * Playing a letter's sound, looked up by the same vocabulary class id as everything else.
 *
 * The clips are base64 data URIs compiled into the bundle, so nothing here fetches and nothing
 * here needs the network. What this module actually owns is the two ways playing a sound can go
 * wrong without anyone noticing:
 *
 * * **No clip.** `illegible` and `unknown` have none and never will — they are not sounds. A
 *   caller gets `false` and must render no control at all, rather than a dead one that beeps.
 * * **No audio at all.** jsdom has no `Audio`, and a browser can refuse playback until the user
 *   has interacted with the page. Both are feature-detected and swallowed, in the convention every
 *   other browser API in this app follows: degrade to a working-but-silent UI, never throw.
 *
 * One element is reused so a fast run down the chart cuts the previous sound off instead of
 * layering twenty of them.
 *
 * What the clips are, and are not, is in `phonologyAudio.generated.ts`: synthesized offline, each
 * consonant framed by two support vowels that are a presentation device and not data.
 */

import type { LetterClip } from './phonologyAudio.generated';
import { soundValue } from './phonology';

/**
 * The audio payload is ~47 KB gzipped of base64 and is fetched on first play, not on load.
 *
 * Everything a caller needs in order to *decide* whether to offer a play control — whether a
 * clip exists and what frame it uses — is on the sound-value table, which is small. So the
 * queue and the browser, which never play anything, pay nothing for this feature. Shipping it
 * eagerly would have contradicted the argument `render_clips.mjs` makes for keeping the
 * synthesizer itself out of the bundle.
 */
let payload: ReadonlyMap<number, LetterClip> | null = null;

async function clips(): Promise<ReadonlyMap<number, LetterClip>> {
  if (!payload) {
    const module = await import('./phonologyAudio.generated');
    payload = new Map(module.LETTER_CLIPS.map((clip) => [clip.letterClass, clip]));
  }
  return payload;
}

let element: HTMLAudioElement | null = null;

/** The clip for a class once the payload is loaded, or `undefined`. */
export async function clipFor(letterClass: number): Promise<LetterClip | undefined> {
  return (await clips()).get(letterClass);
}

/**
 * Whether this class has a sound to play. Synchronous, so markup can decide without awaiting.
 *
 * Read off the sound-value table rather than the payload: a clip exists exactly when the class
 * is a letter the synthesizer was given a mnemonic for, and `illegible`/`unknown` never are.
 */
export function hasClip(letterClass: number): boolean {
  return soundValue(letterClass)?.hasClip ?? false;
}

/**
 * Play a letter's sound. Resolves `false` when there was nothing to play or playback was
 * refused, so a caller can surface that rather than assume it worked.
 */
export async function playLetter(letterClass: number): Promise<boolean> {
  if (!hasClip(letterClass)) return false;
  if (typeof Audio === 'undefined') return false;

  try {
    const clip = (await clips()).get(letterClass);
    if (!clip) return false;
    if (!element) element = new Audio();
    element.pause();
    element.src = clip.clip;
    element.currentTime = 0;
    await element.play();
    return true;
  } catch {
    // Autoplay policy, a missing codec, or no audio device. None of these is worth an error
    // state on a reference chart — the value and its IPA are still on screen.
    return false;
  }
}

/** Stop whatever is playing. Safe to call when nothing is. */
export function stopLetter(): void {
  try {
    element?.pause();
  } catch {
    /* nothing to stop */
  }
}

/**
 * What a listener should be told before trusting the sound.
 *
 * Returns `null` when there is nothing to qualify. Two things can need saying: the clip is framed
 * by support vowels that are not part of the letter, and — for four of the emphatics — the
 * synthesizer does not actually produce the value the table gives.
 */
export function clipCaveat(
  letterClass: number,
  produces: string | null,
  ipa: string | null,
): string | null {
  const frame = soundValue(letterClass)?.clipFrame;
  if (!frame) return null;
  const parts = [
    `heard between two support vowels (${frame.replace('_', 'C')}), which are not part of the letter`,
  ];
  if (produces && ipa && produces !== ipa) {
    parts.push(`the synthesizer approximates [${ipa}] as [${produces}]`);
  }
  return parts.join('; ');
}
